/**
 * Типы домена. Зеркало `ios/Sticker/Models` и схемы Prisma: подписи
 * статусов и правила прогресса существуют в трёх экземплярах и обязаны
 * совпадать — иначе клиенты молча разойдутся с сервером.
 */

export type Role = 'admin' | 'moderator' | 'collector'

export type ItemKind = 'sticker' | 'card' | 'other'

export type TradeStatus =
  | 'draft'
  | 'sent'
  | 'accepted'
  | 'declined'
  | 'cancelled'
  | 'completed'

/// Направление считается от инициатора обмена.
export type TradeDirection = 'give' | 'take'

export interface Session {
  token: string
  user: UserProfile
}

export interface UserProfile {
  id: string
  login: string
  displayName: string
  city: string | null
  avatarUrl: string | null
  rating: number
  tradesCount: number
  role: Role
}

/// Страница списка. Каталог — одиннадцать тысяч коллекций, целиком он
/// не отдаётся ни на одном экране.
export interface Page<T> {
  items: T[]
  total: number
  page: number
  limit: number
}

/// Узел дерева категорий. Дерево, а не два уровня: у кино подкатегорий
/// несколько десятков, и глубина заранее не задана.
export interface CategoryNode {
  id: string
  slug: string
  title: string
  /// Коллекции непосредственно в этой категории, без потомков.
  collectionsCount: number
  children: CategoryNode[]
}

export interface Publisher {
  slug: string
  title: string
  country: string | null
}

export interface Collection {
  id: string
  slug: string
  title: string
  year: number | null
  coverUrl: string | null
  publisher: string | null
  category: string
  itemsTotal: number
  /// Сколько уникальных предметов уже есть у текущего пользователя.
  ownedCount?: number
}

export interface CollectionItem {
  id: string
  number: string
  title: string | null
  kind: ItemKind
  imageUrl: string | null
  /// Состояние в инвентаре: 0 — нет, 1 — есть, >1 — дубли.
  haveCount: number
  isWanted: boolean
}

export interface MatchPartner {
  user: UserProfile
  /// Сколько предметов он может отдать мне и сколько я ему.
  givesCount: number
  takesCount: number
  /// Ценность пары = min(gives, takes): обмен «10 на 1» неинтересен.
  score: number
  sameCity: boolean
}

export interface TradeOffer {
  id: string
  status: TradeStatus
  partner: UserProfile
  give: CollectionItem[]
  take: CollectionItem[]
  createdAt: string
}

export interface Auction {
  id: string
  title: string
  currentPrice: number
  endsAt: string
  bidsCount: number
}

export interface ForumSection {
  id: string
  slug: string
  title: string
  topicsCount: number
}

/// Подписи ролей. Роль показывается в кабинете рядом с логином: она
/// объясняет, почему у модератора в каталоге есть кнопки, которых нет у
/// остальных.
export const ROLE_TITLES: Record<Role, string> = {
  admin: 'Администратор',
  moderator: 'Модератор',
  collector: 'Коллекционер'
}

/// Подписи статусов обмена. Единственный источник для веба.
export const TRADE_STATUS_TITLES: Record<TradeStatus, string> = {
  draft: 'Черновик',
  sent: 'Отправлено',
  accepted: 'Принято',
  declined: 'Отклонено',
  cancelled: 'Отменено',
  completed: 'Завершено'
}

/// Однобуквенные коды для плотных списков — как коды типов заявок в
/// nox-assist: состояние читается без цвета.
export const TRADE_STATUS_CODES: Record<TradeStatus, string> = {
  draft: 'Ч',
  sent: 'О',
  accepted: 'П',
  declined: 'Х',
  cancelled: 'О',
  completed: 'З'
}
